import { Alert, Grid, TableBody, TableCell, TableHead, TableRow, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { LEProps } from "./interfaces";
import SingleLegalEntityProperty from "./SingleLegalEntityProperty";
import { StyledTable } from "./styles";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import useAuthorization from "../../../../../custom-hooks/useAuthorization/useAuthorization";
import { CompanyLegalEntityDTO } from "../../../../../models/company-accounts/company-legal-entities";
import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { CompanyLegalEntitiesApiService } from "../../../../../services/company-legal-entities";
import { AUTHORIZATIONS } from "../../../../../utils/constants/authorization-constants";

const AddLegalEntityFromDatabaseField = ({
  selectedItem,
  selectedItemProperty,
  updateSelectedItem,
  updateSelectedItemProperty,
  getLEPropertyValue,
  selectedLegalEntityPropertyKey,
}: LEProps) => {
  const { t } = useTranslation();
  const tRPLegalEntity = "pages.system_settings.tables.company_legal_entities.fields";
  const tRP = "components.document_templates.prefill_and_generate_document_from_template";

  const theme = useTheme();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const hasAdminAccess = useAuthorization([AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess]);

  const authorizedLegalEntities = currentUserAuthorizations?.allowedDomainsPerLE
    ?.filter(
      (allowedDoamainPerLe) =>
        !!allowedDoamainPerLe.value?.find(
          (auth) =>
            auth === AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting ||
            auth === AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll
        )
    )
    .map((alloweDomainPerLE) => alloweDomainPerLE.key);

  const legalEntityProperties: {
    label: string;
    value: keyof CompanyLegalEntityDTO;
    key?: keyof CompanyLegalEntityDTO[keyof CompanyLegalEntityDTO];
  }[] = [
    { label: `${t(`common_words.name`)}`, value: "name" },
    { label: `${t(`common_words.code`)}`, value: "code" },
    { label: `${t(`common_words.tax_id`)}`, value: "taxId" },
    { label: `${t(`${tRPLegalEntity}.registered_name`)}`, value: "registeredName" },
    { label: `${t(`${tRPLegalEntity}.registered_address_line_one`)}`, value: "registeredAddressLineOne" },
    { label: `${t(`${tRPLegalEntity}.registered_address_line_two`)}`, value: "registeredAddressLineTwo" },
    { label: `${t(`${tRPLegalEntity}.region_or_state`)}`, value: "regionOrState" },
    {
      label: `${t(`${tRPLegalEntity}.registered_address_country`)}`,
      value: "registeredAddressCountry",
      key: "name" as keyof CompanyLegalEntityDTO[keyof CompanyLegalEntityDTO],
    },
  ];

  return (
    <Grid style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "10px 0px" }}>
      <AutocompleteDropdown
        field={"legalEntity"}
        selectedValue={selectedItem}
        serviceApi={new CompanyLegalEntitiesApiService()}
        handleChange={(_, value: CompanyLegalEntityDTO) => updateSelectedItem(value)}
        propsFilter={{
          contextAuthorizations: [
            AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
            AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
          ],
        }}
      />
      {selectedItem ? (
        <>
          <Alert severity="info">{`${t(`${tRP}.selected_legal_entity_hint`)}`} </Alert>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell align="center">{`${t(`${tRP}.legal_entity_property`)}`}</TableCell>
                <TableCell align="center">{`${t(`${tRP}.property_value`)}`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {legalEntityProperties.map((property, idx) => {
                return (
                  <SingleLegalEntityProperty
                    key={idx}
                    propertyLabel={property.label}
                    property={property.value}
                    propertyValue={getLEPropertyValue(property.value)}
                    updateSelectedItemProperty={updateSelectedItemProperty}
                    isSelected={
                      selectedItemProperty === property.value && selectedLegalEntityPropertyKey === property.key
                    }
                  />
                );
              })}
            </TableBody>
          </StyledTable>
        </>
      ) : (
        <></>
      )}
    </Grid>
  );
};

export default AddLegalEntityFromDatabaseField;
